/* ==========================================================================
   Jane Lushka v1.2: the bag (cart drawer) behaviour.
   Loaded by sections/cart-drawer.liquid with defer, after cart-drawer.js and
   cart.js. Plain ES5, no dependencies, never calls fetch: the theme scripts
   stay in charge of the cart (cart-drawer.js renders after an add, cart.js
   after a quantity change or removal, jl-cart-extras.js keeps the header count
   and the "Checking out..." state).

   1) "Continue shopping" ([data-jl-bag-close]) closes the bag. Delegated,
      because cart.js re-renders the footer that holds it.
   2) CartDrawer.renderContents() (after an add): after the re-render,
      highlights the line that was just added, scrolls it to the
      top of the list and announces it. CartDrawer.open(): list back at the
      top (unless an add just placed it), focus into the bag and a jl:bag-open
      event for GA4.
   3) "Shop the look" rows (phase 4, [data-jl-bag-look], Liquid or the related
      fallback that jl-size-sheet.js fills): jl:bag-upsell-shown { count } when
      the bag opens with rows, and again when an open bag gets new rows. Clicks
      inside the shared size sheet ([data-jl-qa-sheet], opened from a row) keep
      the bag open and are never its opener.
   4) Focus after a removal: the Remove link of the line that takes its place
      (else the previous line, the empty-bag button, the close button), plus a
      "Removed from your bag" announcement. Other quantity changes announce
      cart.js's stock message or the new subtotal.
   5) Keyboard: Tab and Shift+Tab stay inside the open bag, and focus goes back
      to what opened it when the bag closes.
   6) A cookie or consent banner fixed to the bottom of the screen gets room
      under the footer while the bag is open, so it never covers Checkout, and
      Accept or Decline on it no longer closes the bag.
   7) "Your bag (N)" links to the bag page only while the bag has items:
      cart.js never re-renders the header, so the last removal takes the
      link off here (an add re-renders it from Liquid).
   8) Express checkout row (Shopify's wallet buttons): Tab goes on from
      Checkout into the buttons and wraps back to the top when it leaves them,
      through an empty focus stop at the end of the bag (they live in a closed
      shadow DOM or a frame, out of reach of the list in 5); Shift+Tab from the
      top goes through that stop into the last button. A row that arrives over
      AJAX on a page that loaded with an empty bag gets Shopify's loader nudged.
   GA4 (snippets/ga4-custom-events.liquid) listens for jl:bag-open and
   jl:bag-upsell-shown on document.
   ========================================================================== */
(function () {
  'use strict';

  var host = document.querySelector('cart-drawer');
  if (!host) return;

  var ADD_WINDOW_MS = 3000;     // an open() this soon after an add counts as trigger 'add'
  var JUST_ADDED_MS = 2100;     // .is-just-added lifetime (the CSS fade is .2s delay + 1.6s)
  var REMOVAL_TTL_MS = 5000;    // forget a pending removal after this long
  var PRESS_TTL_MS = 10000;     // a control pressed this recently can be the opener

  var lastAddAt = 0;
  var addPositioned = false;
  var upsellShownThisOpen = false;
  var upsellShownBlock = null;
  var pendingRemoval = null;
  var removalTimer = null;
  var observer = null;
  var opener = null;
  var lastPressed = null;
  var lastPressedAt = 0;
  var EXPRESS_NUDGE_LIMIT = 3;  // Shopify.PaymentButton.init() at most this often per page view
  var tabMove = 0;              // 1 or -1 (Shift) only while the browser moves focus for a Tab
  var endPass = false;          // the end stop is being focused on purpose (Shift+Tab into the wallets)
  var expressNudges = 0;

  /* ---- helpers ---------------------------------------------------------- */
  function drawerEl() { return host.querySelector('[data-cart-drawer]'); }
  function contentEl() { return host.querySelector('.cart-drawer__content'); }
  function inSheet(el) { return !!(el && el.closest && el.closest('[data-jl-qa-sheet]')); }
  function isOpen() { return document.body.classList.contains('cart-drawer-is-open'); }
  function isEmptyBag() { return !!host.querySelector('cart-items.cart-drawer-is-empty'); }

  function lines() {
    return Array.prototype.slice.call(host.querySelectorAll('.jl-bag-line'));
  }

  function findLine(key) {
    if (!key) return null;
    var all = lines();
    for (var i = 0; i < all.length; i++) {
      if (all[i].getAttribute('data-line-key') === key) return all[i];
    }
    return null;
  }

  function isVisible(el) {
    return !!(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));
  }

  function textOf(el) {
    return el ? (el.textContent || '').replace(/\s+/g, ' ').replace(/^\s+|\s+$/g, '') : '';
  }

  function focusEl(el) {
    try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
  }

  // The list scrolls; on very short screens (CSS, max-height 340px, 440px from 768px wide) the whole drawer does.
  function scroller() {
    var content = contentEl();
    if (content && /auto|scroll/.test(window.getComputedStyle(content).overflowY)) return content;
    return drawerEl();
  }

  // Liquid escapes the data-jl-added/-removed templates; if a translation was
  // escaped twice, decode once more so a screen reader never reads "&amp;".
  function decodeEntities(text) {
    if (!/&[#a-z0-9]+;/i.test(text)) return text;
    var ta = document.createElement('textarea');
    ta.innerHTML = text;
    return ta.value;
  }

  function message(attr, title) {
    var tpl = host.getAttribute(attr) || '';
    if (!tpl) return '';
    return decodeEntities(tpl).replace('[title]', title || '');
  }

  // Polite live region outside the cart.js swaps. Cleared first, so the same
  // message twice in a row is still read.
  function announce(text) {
    if (!text) return;
    var el = host.querySelector('[data-jl-bag-status]');
    if (!el) return;
    el.textContent = '';
    setTimeout(function () {
      var now = host.querySelector('[data-jl-bag-status]');
      if (now) now.textContent = text;
    }, 100);
  }

  function dispatch(name, detail) {
    var ev;
    try {
      ev = new CustomEvent(name, { detail: detail });
    } catch (e) {
      ev = document.createEvent('CustomEvent');
      ev.initCustomEvent(name, false, false, detail);
    }
    document.dispatchEvent(ev);
  }

  function readTotals() {
    var out = { item_count: 0, value: 0, currency: '' };
    var el = host.querySelector('.jl-bag-totals');
    if (el) {
      var cents = parseInt(el.getAttribute('data-bag-value'), 10);
      out.item_count = parseInt(el.getAttribute('data-bag-count'), 10) || 0;
      out.value = isNaN(cents) ? 0 : cents / 100;   // major units
      out.currency = el.getAttribute('data-bag-currency') || '';
    } else if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      out.currency = window.Shopify.currency.active;
    }
    return out;
  }

  // Drop the mutation records our own DOM moves just produced.
  function discardOwnMutations() {
    if (observer) observer.takeRecords();
  }

  /* ---- 1) Continue shopping --------------------------------------------- */
  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t || !t.closest) return;
    var btn = t.closest('[data-jl-bag-close]');
    if (!btn || !host.contains(btn)) return;
    event.preventDefault();
    if (typeof host.close === 'function') host.close();
  });

  /* ---- 3) "Shop the look" rows ------------------------------------------- */
  // Once per opening while rows are on screen, and again when an open bag gets a new
  // block (an add, a cart.js update, the related fallback arriving).
  function maybeUpsellShown() {
    if (!isOpen() || isEmptyBag()) return;
    var block = host.querySelector('[data-jl-bag-look]');
    var count = block && isVisible(block) ? block.querySelectorAll('[data-jl-look-row]').length : 0;
    if (!count || (upsellShownThisOpen && block === upsellShownBlock)) return;
    upsellShownThisOpen = true;
    upsellShownBlock = block;
    dispatch('jl:bag-upsell-shown', { count: count });
  }

  /* ---- 4) focus after a removal, announcements -------------------------- */
  function rememberRemoval(line) {
    pendingRemoval = {
      key: line.getAttribute('data-line-key'),
      title: line.getAttribute('data-line-title') || '',
      position: lines().indexOf(line)
    };
    clearTimeout(removalTimer);
    removalTimer = setTimeout(function () { pendingRemoval = null; }, REMOVAL_TTL_MS);
  }

  // Capture phase: runs before cart.js and global.js change anything.
  host.addEventListener('click', function (event) {
    var t = event.target;
    if (!t || !t.closest) return;
    var line = t.closest('.jl-bag-line');
    if (!line) return;
    var removes = !!t.closest('cart-remove-button');
    if (!removes && t.closest('.quantity__button[name="minus"]')) {
      var input = line.querySelector('input[name="updates[]"]');
      removes = !!input && (parseInt(input.value, 10) || 0) <= 1;
    }
    if (removes) rememberRemoval(line);
  }, true);

  // Typing 0 in the quantity field removes the line too.
  host.addEventListener('change', function (event) {
    var t = event.target;
    if (!t || !t.matches || !t.matches('input[name="updates[]"]')) return;
    var line = t.closest('.jl-bag-line');
    if (line && (parseInt(t.value, 10) || 0) <= 0) rememberRemoval(line);
  }, true);

  // Returns true when it announced a removal.
  function processRemoval() {
    var p = pendingRemoval;
    if (!p || findLine(p.key)) return false;   // nothing pending, or the line is still there
    pendingRemoval = null;
    clearTimeout(removalTimer);
    announce(message('data-jl-removed', p.title));

    var drawer = drawerEl();
    if (!drawer || !isOpen()) return true;
    var active = document.activeElement;
    // cart.js already put focus on a control in the bag (minus at quantity 1): leave it there.
    if (active && active !== document.body && active !== drawer && drawer.contains(active)) return true;

    var target = null;
    var all = lines();
    if (all.length) {
      var line = all[p.position] || all[p.position - 1] || all[all.length - 1];
      target = line ? line.querySelector('.jl-bag-line__remove') : null;
    }
    if (!target) {
      var cta = host.querySelector('[data-jl-bag-empty-cta]');
      if (isVisible(cta)) target = cta;
    }
    if (!target) target = host.querySelector('[data-cart-button-close]');
    if (target) target.focus();
    return true;
  }

  // A cart.js update that removed nothing (WCAG 4.1.3): read cart.js's message (a stock
  // limit on the line, or the cart error), else the new subtotal. One message per update.
  function processCartUpdate(records) {
    var swapped = false;
    var errored = false;
    for (var i = 0; i < records.length; i++) {
      var t = records[i].target;
      if (records[i].type !== 'childList' || !t || t.nodeType !== 1) continue;
      if (t.classList.contains('js-cart-footer-contents')) swapped = true;
      else if (t.id === 'cart-errors') errored = true;
    }
    if (!swapped && !errored) return;
    var msg = '';
    var errs = host.querySelectorAll('.cart-item__error-text, #cart-errors');
    for (var e = 0; e < errs.length && !msg; e++) msg = textOf(errs[e]);
    if (!msg && swapped) msg = textOf(host.querySelector('.jl-bag-totals__row'));
    announce(msg);
  }

  /* ---- 5) keyboard: Tab stays in the bag, focus in and back -------------- */
  // global.js trapFocus() keeps the first and last control it found when the bag opened,
  // hidden ones included (Continue shopping is display:none on phones) and ones cart.js has
  // since replaced. The list is worked out again on every Tab instead.
  var FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), ' +
    'select:not([disabled]), textarea:not([disabled]), iframe, summary, [tabindex]';

  function tabbables(root) {
    var all = root.querySelectorAll(FOCUSABLE);
    var out = [];
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.tabIndex < 0 || !el.getClientRects().length || el.hasAttribute('data-jl-focus-end')) continue;
      if (window.getComputedStyle(el).visibility === 'hidden') continue;
      out.push(el);
    }
    return out;
  }

  // 8) The express row: Shopify's wallet buttons are focusable, but inside a closed shadow
  // root, so tabbables() cannot list them. When the row is drawn after the control that
  // looks last (Checkout on phones, where Continue shopping is hidden), Tab is left to the
  // browser so it moves on into the buttons.
  function expressRow() {
    var row = host.querySelector('[data-jl-express]');
    if (!row || !row.getClientRects().length || row.getBoundingClientRect().height < 1) return null;
    return row;
  }

  function expressAfter(el) {
    var row = expressRow();
    if (!row || row.contains(el)) return false;
    return !!(el.compareDocumentPosition(row) & 4);   // Node.DOCUMENT_POSITION_FOLLOWING
  }

  // The bag ends in an empty focus stop, [data-jl-focus-end] (last child of the dialog in
  // sections/cart-drawer.liquid, so both re-render paths keep it). Leaving the last wallet
  // button with Tab always lands on it, also when that wallet is drawn in a frame (PayPal's
  // buttons are an iframe: a key pressed in there never reaches this page), and it hands
  // focus on to the first control. It is never a stop of its own (not in tabbables()).
  function focusEnd() { return host.querySelector('[data-jl-focus-end]'); }

  host.addEventListener('focusin', function (event) {
    var t = event.target;
    if (endPass || !t || !t.hasAttribute || !t.hasAttribute('data-jl-focus-end')) return;
    var drawer = drawerEl();
    var els = drawer && isOpen() ? tabbables(drawer) : [];
    if (els.length) focusEl(els[0]);
  });

  // Capture phase on <cart-drawer> (never re-rendered): runs before global.js's handler on
  // document, which is stopped whenever this one wraps.
  host.addEventListener('keydown', function (event) {
    if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey || !isOpen()) return;
    // (a Tab on a wallet button reaches here too: keydown leaves a shadow root, not a frame)
    // Armed for this Tab's own focus move only, which the browser makes before any timer runs.
    tabMove = event.shiftKey ? -1 : 1;
    setTimeout(function () { tabMove = 0; }, 0);
    var drawer = drawerEl();
    if (!drawer) return;
    var els = tabbables(drawer);
    if (!els.length) return;
    var active = document.activeElement;
    var inside = drawer.contains(active) && active !== drawer;
    var target = null;
    if (event.shiftKey) {
      if (!inside || active === els[0]) {
        var end = focusEnd();
        if (end && expressAfter(els[els.length - 1])) {
          // The wallets come last: move to the end stop and let the browser's own Shift+Tab
          // go on from there into the last wallet button (shadow DOM or frame alike).
          endPass = true;
          focusEl(end);
          endPass = false;
          event.stopPropagation();
          return;
        }
        target = els[els.length - 1];
      }
    } else if (!inside && active !== drawer) {
      target = els[0];
    } else if (active === els[els.length - 1] && !expressAfter(active)) {
      target = els[0];
    }
    if (!target) return;
    event.preventDefault();
    event.stopPropagation();
    target.focus();
  }, true);

  // Backstop: a Tab whose own focus move still leaves the bag (from a control in it to the
  // page behind) is wrapped: forward to the first control, backward to the last. Nothing
  // else counts, so a click on the consent banner or a wallet's own dialog keeps its focus.
  document.addEventListener('focusin', function (event) {
    if (!isOpen() || !tabMove) return;
    var t = event.target;
    var from = event.relatedTarget;
    if (!t || host.contains(t) || inSheet(t) || !from || !host.contains(from)) return;
    var drawer = drawerEl();
    var els = drawer ? tabbables(drawer) : [];
    if (!els.length) return;
    var back = tabMove < 0;
    tabMove = 0;
    focusEl(back ? els[els.length - 1] : els[0]);
  });

  // What opened the bag gets focus back when it closes. A button that was just pressed
  // counts too: product-form.js disables the Add button while it adds, which drops focus.
  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t || !t.closest || host.contains(t) || inSheet(t)) return;
    var c = t.closest('button, a[href], [role="button"], input[type="submit"], input[type="button"]');
    if (c) {
      lastPressed = c;
      lastPressedAt = Date.now();
    }
  }, true);

  function bagButton() {
    var bubble = document.querySelector('cart-icon-bubble');
    return (bubble && bubble.toggleButton) || document.getElementById('cart-icon-bubble');
  }

  function canFocus(el) {
    return !!(el && document.documentElement.contains(el) && !host.contains(el) && !el.disabled && el.getClientRects().length);
  }

  function rememberOpener() {
    var a = document.activeElement;
    if (a && a !== document.body && a !== document.documentElement && !host.contains(a)) opener = a;
    else if (lastPressed && Date.now() - lastPressedAt < PRESS_TTL_MS && !host.contains(lastPressed)) opener = lastPressed;
    else opener = null;
  }

  function returnFocus() {
    var target = canFocus(opener) ? opener : bagButton();
    opener = null;
    if (canFocus(target)) focusEl(target);
  }

  // cart-drawer.js focuses the dialog while it is still visibility:hidden (start of the
  // slide-in), so the focus does not land. Try again once it is visible.
  function focusIntoBag() {
    var tries = 0;
    var raf = window.requestAnimationFrame || function (fn) { return setTimeout(fn, 16); };
    function attempt() {
      var drawer = drawerEl();
      if (!drawer || !isOpen()) return;
      var a = document.activeElement;
      if (a && drawer.contains(a)) return;   // already inside (the dialog itself counts)
      focusEl(drawer);
      if (document.activeElement !== drawer && ++tries < 10) setTimeout(attempt, 60);
    }
    raf(function () { raf(attempt); });
  }

  // Every way of closing (close button, Escape, overlay, bag icon, Continue shopping) ends
  // in the body class going; the overlay removes it without calling close().
  function onClosed() {
    var a = document.activeElement;
    if (!a || a === document.body || a === document.documentElement || host.contains(a)) returnFocus();
    else opener = null;
  }

  /* ---- consent banner inset ---------------------------------------------- */
  // A cookie or consent banner fixed to the bottom of the screen (first visit) covers
  // Checkout. While the bag is open its height becomes padding under the footer
  // (--jl-bag-inset on the drawer), and the padding goes when the banner does.
  var BANNER_SELECTOR = '#shopify-pc__banner, .shopify-pc__banner__dialog, #CybotCookiebotDialog, #onetrust-banner-sdk, .cc-window, ' +
    '[id*="cookie" i], [class*="cookie" i], [id*="consent" i], [class*="consent" i], [id*="gdpr" i], [class*="gdpr" i]';
  var BANNER_FALLBACK = '#shopify-pc__banner, .shopify-pc__banner__dialog, #CybotCookiebotDialog, #onetrust-banner-sdk, .cc-window, ' +
    '[id*="cookie"], [class*="cookie"], [id*="consent"], [class*="consent"], [id*="gdpr"], [class*="gdpr"]';
  var insetTimer = null;

  function isFixed(el) {
    for (var n = el, depth = 0; n && n !== document.body && depth < 6; n = n.parentElement, depth++) {
      var pos = window.getComputedStyle(n).position;
      if (pos === 'fixed' || pos === 'sticky') return true;
    }
    return false;
  }

  function bannerInset(drawer) {
    var list;
    try { list = document.querySelectorAll(BANNER_SELECTOR); } catch (e) { list = document.querySelectorAll(BANNER_FALLBACK); }
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var d = drawer.getBoundingClientRect();
    var inset = 0;
    for (var i = 0; i < list.length; i++) {
      var el = list[i];
      if (host.contains(el) || el.contains(host)) continue;
      var r = el.getBoundingClientRect();
      // bottom edge on screen, not a full-screen wrapper, overlapping the drawer
      if (!r.width || !r.height || r.bottom < vh - 4 || r.top >= vh || r.height > vh * 0.75) continue;
      if (r.right <= d.left || r.left >= d.right) continue;
      var cs = window.getComputedStyle(el);
      if (cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0 || !isFixed(el)) continue;
      inset = Math.max(inset, vh - r.top);
    }
    return Math.min(Math.round(inset), Math.round(vh * 0.6));
  }

  function updateInset() {
    var drawer = drawerEl();
    if (!drawer) return;
    var inset = isOpen() ? bannerInset(drawer) : 0;
    if (inset > 0) {
      drawer.style.setProperty('--jl-bag-inset', inset + 'px');
      if (!drawer.hasAttribute('data-jl-inset')) drawer.setAttribute('data-jl-inset', '');
    } else if (drawer.hasAttribute('data-jl-inset')) {
      drawer.removeAttribute('data-jl-inset');
      drawer.style.removeProperty('--jl-bag-inset');
    }
  }

  function inBanner(target) {
    if (!target || !target.closest || host.contains(target)) return false;
    try { return !!target.closest(BANNER_SELECTOR); } catch (e) { return !!target.closest(BANNER_FALLBACK); }
  }

  // cart-drawer.js closes the bag on any click outside it, so Accept or Decline on the
  // banner used to close the bag too. The bound handler is replaced on the element
  // (the constructor bound it before this file could patch the prototype).
  function keepOpenOnBannerClicks(el) {
    if (!el || el.__jlBannerClicks || typeof el.handleBodyClick !== 'function' || isOpen()) return;
    el.__jlBannerClicks = true;
    el.onBodyClick = function (event) {
      if (inBanner(event && event.target) || inSheet(event && event.target)) return;
      el.handleBodyClick(event);
    };
  }

  // Banners slide in and out, so check again a little later as well.
  function scheduleInset(delay) {
    if (insetTimer) clearTimeout(insetTimer);
    insetTimer = setTimeout(function () { insetTimer = null; updateInset(); }, delay || 0);
  }

  /* ---- 7) "Your bag (N)" links to the bag page while the bag has items ---- */
  // The section renders <a data-jl-bag-page-link> with href and aria-labelledby only when
  // the cart has items. cart.js swaps the lines and the footer but never the header, so
  // after the last removal the title would still link to an empty bag page: take href and
  // aria-labelledby off (an <a> without href is plain text, out of the Tab order), and put
  // them back from the data attributes if the lines come back without a full render.
  // (Attribute changes on the link are not in the observer's filter: no feedback loop.)
  function syncPageLink() {
    var link = host.querySelector('[data-jl-bag-page-link]');
    if (!link) return;
    var hasItems = !isEmptyBag() && !!host.querySelector('.jl-bag-line');
    var href = link.getAttribute('data-jl-href');
    var labelledby = link.getAttribute('data-jl-labelledby');
    if (hasItems && href) {
      if (link.getAttribute('href') !== href) link.setAttribute('href', href);
      if (labelledby && link.getAttribute('aria-labelledby') !== labelledby) link.setAttribute('aria-labelledby', labelledby);
    } else if (!hasItems) {
      if (link.hasAttribute('href')) link.removeAttribute('href');
      if (link.hasAttribute('aria-labelledby')) link.removeAttribute('aria-labelledby');
    }
  }

  /* ---- 8) express checkout row: Shopify's loader ------------------------- */
  // Shopify documents that the Section Rendering API re-renders the buttons and that they
  // draw more slowly the first time, while its script downloads and registers the custom
  // elements (<shopify-accelerated-checkout-cart>): nothing to call when the page already
  // had buttons. A row that arrives over AJAX while those elements are still undefined (the
  // page loaded with an empty bag, so no buttons were on it) gets the same call Shopify's
  // Dawn theme makes after it inserts payment buttons, Shopify.PaymentButton.init(), when
  // Shopify provides it; at most a few times per page view. Each row is looked at once.
  var EXPRESS_SEEN_ATTR = 'data-jl-express-seen';

  function undefinedShopifyElements(row) {
    var out = [];
    if (!window.customElements) return out;
    var all = row.querySelectorAll('*');
    for (var i = 0; i < all.length; i++) {
      var name = all[i].localName;
      if (name.indexOf('shop') === 0 && name.indexOf('-') > 0 && !window.customElements.get(name)) out.push(all[i]);
    }
    return out;
  }

  function nudgeExpress(atStart) {
    var row = host.querySelector('[data-jl-express]');
    if (!row || row.hasAttribute(EXPRESS_SEEN_ATTR)) return;
    row.setAttribute(EXPRESS_SEEN_ATTR, '');
    if (atStart) return;   // rendered with the page: Shopify's own script draws it
    if (!undefinedShopifyElements(row).length) return;
    var pb = window.Shopify && window.Shopify.PaymentButton;
    if (!pb || typeof pb.init !== 'function' || expressNudges >= EXPRESS_NUDGE_LIMIT) return;
    expressNudges += 1;
    try { pb.init(); } catch (e) { /* Shopify's script failed: the row stays empty, Checkout works */ }
  }

  /* ---- 2) patch CartDrawer ---------------------------------------------- */
  function addedKeys(state) {
    var keys = [];
    if (!state) return keys;
    if (state.key) keys.push(String(state.key));
    if (state.items && state.items.length) {
      for (var i = 0; i < state.items.length; i++) {
        if (state.items[i] && state.items[i].key) keys.push(String(state.items[i].key));
      }
    }
    return keys;
  }

  function markJustAdded(line) {
    line.classList.add('is-just-added');
    setTimeout(function () { line.classList.remove('is-just-added'); }, JUST_ADDED_MS);
  }

  function scrollToLine(line) {
    var box = scroller();
    if (!box) return;
    if (line === lines()[0]) {
      box.scrollTop = 0;
      return;
    }
    var content = contentEl();
    var pad = content ? parseFloat(window.getComputedStyle(content).paddingTop) || 0 : 0;
    var top = line.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop;
    box.scrollTop = Math.max(0, top - pad);
  }

  function afterRender(state) {
    discardOwnMutations();
    maybeUpsellShown();
    syncPageLink();
    nudgeExpress(false);

    // renderContents() only runs after an add (product form, quick add)
    lastAddAt = Date.now();
    addPositioned = false;
    var found = [];
    addedKeys(state).forEach(function (key) {
      var line = findLine(key);
      if (line && found.indexOf(line) === -1) found.push(line);
    });
    if (!found.length) return;
    found.forEach(markJustAdded);
    scrollToLine(found[0]);
    addPositioned = true;
    announce(message('data-jl-added', found.map(function (l) {
      return l.getAttribute('data-line-title') || '';
    }).join(', ')));
  }

  function afterOpen(wasOpen) {
    var recentAdd = lastAddAt && Date.now() - lastAddAt < ADD_WINDOW_MS;
    var box = scroller();
    if (box && !(recentAdd && addPositioned)) box.scrollTop = 0;
    lastAddAt = 0;
    addPositioned = false;
    if (!wasOpen) upsellShownThisOpen = false;

    var totals = readTotals();
    dispatch('jl:bag-open', {
      trigger: recentAdd ? 'add' : 'open',
      item_count: totals.item_count,
      value: totals.value,
      currency: totals.currency
    });
    maybeUpsellShown();
    updateInset();
    scheduleInset(450);
  }

  function patch(Ctor) {
    var proto = Ctor && Ctor.prototype;
    if (!proto || proto.__jlBagPatched) return;
    proto.__jlBagPatched = true;

    var originalRender = proto.renderContents;
    if (typeof originalRender === 'function') {
      proto.renderContents = function (state) {
        var result;
        try {
          result = originalRender.apply(this, arguments);
        } catch (err) {
          // the drawer kept its old markup (for example a section that did not render)
          discardOwnMutations();
          throw err;
        }
        try { afterRender(state); } catch (e) { /* the bag still works without the extras */ }
        return result;
      };
    }

    var originalOpen = proto.open;
    if (typeof originalOpen === 'function') {
      proto.open = function () {
        var wasOpen = isOpen();
        if (!wasOpen) rememberOpener();
        var result = originalOpen.apply(this, arguments);
        try { afterOpen(wasOpen); } catch (e) { /* idem */ }
        try { focusIntoBag(); } catch (e) { /* idem */ }
        return result;
      };
    }
  }

  if (window.customElements && typeof window.customElements.whenDefined === 'function') {
    window.customElements.whenDefined('cart-drawer').then(function () {
      patch(window.customElements.get('cart-drawer'));
      keepOpenOnBannerClicks(host);
    });
  }

  /* ---- observers --------------------------------------------------------- */
  if ('MutationObserver' in window) {
    // <cart-drawer> survives both partial (cart.js) and full (renderContents) re-renders.
    observer = new MutationObserver(function (records) {
      var relevant = false;
      for (var i = 0; i < records.length && !relevant; i++) {
        relevant = records[i].type === 'childList';
      }
      if (!relevant) return;
      maybeUpsellShown();
      syncPageLink();
      nudgeExpress(false);
      if (!processRemoval()) processCartUpdate(records);
    });
    observer.observe(host, { childList: true, subtree: true });

    var wasOpenState = isOpen();
    new MutationObserver(function () {
      var open = isOpen();
      if (open === wasOpenState) return;
      wasOpenState = open;
      if (!open) { onClosed(); updateInset(); }
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });

    // A banner added or removed while the bag is open (most banners are appended to body).
    new MutationObserver(function () {
      if (isOpen()) scheduleInset(50);
    }).observe(document.body, { childList: true });
  }

  // Accept or Decline on the banner, and screen size changes, while the bag is open.
  document.addEventListener('click', function () {
    if (isOpen()) { scheduleInset(400); setTimeout(updateInset, 1200); }
  }, true);
  window.addEventListener('resize', function () {
    if (isOpen()) scheduleInset(100);
  });
  syncPageLink();
  nudgeExpress(true);
})();
